import { useEffect, useState, useCallback, useRef } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  MicrosoftOutlookLogo, UploadSimple, Trash, ShieldCheck, Lightning,
  CheckCircle, XCircle, Warning, DownloadSimple, FileCsv,
} from "@phosphor-icons/react";

const PROVIDER = "outlook_oauth";

export default function OAuthSenders() {
  const [busy, setBusy] = useState(false);
  const [file, setFile] = useState(null);
  const [result, setResult] = useState(null);
  const fileRef = useRef(null);
  const [senders, setSenders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [testTo, setTestTo] = useState({}); // sender_id -> recipient email
  const [rowBusy, setRowBusy] = useState({}); // sender_id -> bool
  const [nameEdit, setNameEdit] = useState({}); // sender_id -> {first, last}

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/senders", { params: { provider: PROVIDER, limit: 500 } });
      setSenders(data.senders || []);
      setNameEdit((prev) => {
        const next = { ...prev };
        (data.senders || []).forEach((s) => {
          if (!next[s.id]) next[s.id] = { first: s.first_name || "", last: s.last_name || "" };
        });
        return next;
      });
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const downloadSample = async () => {
    try {
      const { data } = await api.get("/oauth-senders/sample-csv", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = "outlook-oauth-senders-sample.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    }
  };

  const doImport = async () => {
    if (!file) { toast.error("Choose a CSV file first"); return; }
    setBusy(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/oauth-senders/upload-csv", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data);
      toast.success(`Imported ${data.inserted} · skipped ${data.skipped_duplicate} dup / ${data.skipped_invalid} invalid`);
      setFile(null);
      if (fileRef.current) fileRef.current.value = "";
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const setBusyFor = (id, v) => setRowBusy((p) => ({ ...p, [id]: v }));

  const saveName = async (id) => {
    setBusyFor(id, true);
    try {
      const n = nameEdit[id] || { first: "", last: "" };
      await api.patch(`/senders/${id}`, { first_name: n.first, last_name: n.last });
      toast.success("Name saved");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally {
      setBusyFor(id, false);
    }
  };

  const testProxy = async (id) => {
    setBusyFor(id, true);
    try {
      const { data } = await api.post(`/senders/${id}/test-proxy`);
      if (data.success) toast.success(`Proxy OK · IP ${data.public_ip} · ${data.response_time_ms}ms`);
      else toast.error(`Proxy failed: ${data.error_message || data.status}`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally {
      setBusyFor(id, false);
    }
  };

  const testToken = async (id) => {
    setBusyFor(id, true);
    try {
      const to = (testTo[id] || "").trim();
      const { data } = await api.post(`/oauth-senders/${id}/test`, to ? { to } : {});
      if (data.success) {
        toast.success(data.stage === "send" ? `Test email sent to ${to}` : (data.message || "Token acquired — sender is live"));
      } else {
        toast.error(`Test failed (${data.stage}): ${data.error || "unknown"}`);
      }
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally {
      setBusyFor(id, false);
    }
  };

  const del = async (id, email) => {
    if (!window.confirm(`Delete ${email}?`)) return;
    setBusyFor(id, true);
    try {
      await api.delete(`/senders/${id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally {
      setBusyFor(id, false);
    }
  };

  return (
    <div className="p-8" data-testid="oauth-senders-page">
      <div className="mb-6">
        <div className="micro mb-1">Senders</div>
        <h1 className="font-heading text-4xl font-bold tracking-tighter flex items-center gap-3">
          <MicrosoftOutlookLogo size={32} weight="fill" className="text-[#00A4EF]" />
          Outlook OAuth Senders
        </h1>
        <p className="text-xs font-mono text-[#71717A] mt-2 max-w-3xl leading-relaxed">
          Consumer <b className="text-[#E1FF00]">@hotmail.com / @outlook.com</b> mailboxes that authenticate with a
          delegated OAuth <b>refresh token</b> and send via SMTP XOAUTH2 through each account&apos;s proxy — no app
          password needed. These behave as normal senders in any campaign. The password is stored for your records
          but is <b>not</b> used for sending.
        </p>
      </div>

      {/* Bulk import via CSV */}
      <div className="border border-[#2A2A2A] bg-[#0A0A0A] p-5 mb-8">
        <div className="flex items-center gap-2 mb-3">
          <UploadSimple size={16} weight="bold" className="text-[#E1FF00]" />
          <span className="font-mono text-[11px] uppercase tracking-wider text-[#A1A1AA]">Bulk import (CSV)</span>
        </div>
        <p className="text-xs font-mono text-[#71717A] mb-4 leading-relaxed">
          Download the sample, fill one account per row, then upload. Columns:
          {" "}<code className="text-[#00E5FF]">email, password, refresh_token, firstName, lastName, proxyProtocol,
          proxyHost, proxyPort, proxyUsername, proxyPassword</code> (<code className="text-[#00E5FF]">clientId</code> optional
          — defaults to Thunderbird). Without a name, the display name is derived from the email. The password is stored
          for your records only and is <b>not</b> used for sending.
        </p>

        <div className="flex items-center gap-3 flex-wrap">
          <Button data-testid="oauth-download-sample-btn" onClick={downloadSample}
                  variant="outline" className="border-[#2A2A2A]">
            <DownloadSimple size={15} className="mr-2" /> Download sample CSV
          </Button>

          <label className="flex items-center gap-2 px-3 py-2 border border-[#2A2A2A] bg-[#050505] cursor-pointer hover:border-[#3A3A3A] transition-colors"
                 data-testid="oauth-file-label">
            <FileCsv size={15} className="text-[#00E5FF]" />
            <span className="font-mono text-xs text-[#A1A1AA]">{file ? file.name : "Choose CSV file…"}</span>
            <input
              ref={fileRef}
              data-testid="oauth-file-input"
              type="file"
              accept=".csv,text/csv"
              onChange={(e) => { setFile(e.target.files?.[0] || null); setResult(null); }}
              className="hidden"
            />
          </label>

          <Button data-testid="oauth-import-btn" onClick={doImport} disabled={busy || !file}
                  className="bg-[#E1FF00] text-black hover:bg-[#c9e600]">
            {busy ? "Importing…" : "Import CSV"}
          </Button>
        </div>

        {result && (
          <div className="mt-4 grid grid-cols-2 md:grid-cols-3 gap-3" data-testid="oauth-import-result">
            <PreviewStat label="Imported" v={result.inserted} tone="brand" />
            <PreviewStat label="Skipped (dup)" v={result.skipped_duplicate} tone="warning" />
            <PreviewStat label="Skipped (invalid)" v={result.skipped_invalid} tone={result.skipped_invalid ? "error" : "muted"} />
            {result.errors?.length > 0 && (
              <div className="col-span-full text-xs font-mono text-[#FF6B6B] space-y-1 mt-1 max-h-40 overflow-y-auto">
                {result.errors.map((e, i) => (<div key={i}>{e}</div>))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Sender list */}
      <div className="flex items-center justify-between mb-3">
        <span className="font-mono text-[11px] uppercase tracking-wider text-[#A1A1AA]">
          {senders.length} OAuth sender{senders.length !== 1 ? "s" : ""}
        </span>
        <Button variant="ghost" onClick={load} className="text-xs text-[#71717A]">Refresh</Button>
      </div>

      <div className="border border-[#2A2A2A] bg-[#0A0A0A] overflow-x-auto" data-testid="oauth-senders-table">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-[#2A2A2A] text-[#71717A] font-mono text-[11px] uppercase tracking-wider">
              <th className="text-left px-4 py-3">Email</th>
              <th className="text-left px-4 py-3">From name</th>
              <th className="text-left px-4 py-3">Proxy</th>
              <th className="text-left px-4 py-3">Proxy status</th>
              <th className="text-left px-4 py-3">OAuth</th>
              <th className="text-left px-4 py-3">Test recipient</th>
              <th className="text-right px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-[#71717A] font-mono text-xs">Loading…</td></tr>
            ) : senders.length === 0 ? (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-[#71717A] font-mono text-xs">No OAuth senders yet. Import some above.</td></tr>
            ) : senders.map((s) => (
              <tr key={s.id} className="border-b border-[#1A1A1A] hover:bg-[#0D0D0D]" data-testid={`oauth-sender-row-${s.email}`}>
                <td className="px-4 py-3 font-mono text-xs">{s.email}</td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-1">
                    <Input
                      data-testid={`oauth-first-${s.email}`}
                      value={nameEdit[s.id]?.first ?? ""}
                      onChange={(e) => setNameEdit((p) => ({ ...p, [s.id]: { ...(p[s.id] || {}), first: e.target.value } }))}
                      placeholder="First"
                      className="h-8 w-24 font-mono text-[11px] bg-[#050505] border-[#2A2A2A]"
                    />
                    <Input
                      data-testid={`oauth-last-${s.email}`}
                      value={nameEdit[s.id]?.last ?? ""}
                      onChange={(e) => setNameEdit((p) => ({ ...p, [s.id]: { ...(p[s.id] || {}), last: e.target.value } }))}
                      placeholder="Last"
                      className="h-8 w-24 font-mono text-[11px] bg-[#050505] border-[#2A2A2A]"
                    />
                    <Button data-testid={`oauth-save-name-${s.email}`} onClick={() => saveName(s.id)}
                            disabled={rowBusy[s.id]} variant="outline" size="sm"
                            className="border-[#2A2A2A] h-8 text-[11px] px-2">Save</Button>
                  </div>
                </td>
                <td className="px-4 py-3 font-mono text-[11px] text-[#A1A1AA]">
                  {s.proxy_protocol}://{s.proxy_host}:{s.proxy_port}
                </td>
                <td className="px-4 py-3"><ProxyBadge status={s.proxy_status} /></td>
                <td className="px-4 py-3"><OAuthBadge status={s.oauth_status} /></td>
                <td className="px-4 py-3">
                  <Input
                    data-testid={`oauth-test-to-${s.email}`}
                    value={testTo[s.id] || ""}
                    onChange={(e) => setTestTo((p) => ({ ...p, [s.id]: e.target.value }))}
                    placeholder="optional to@…"
                    className="h-8 w-40 font-mono text-[11px] bg-[#050505] border-[#2A2A2A]"
                  />
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-end gap-2">
                    <Button data-testid={`oauth-test-proxy-${s.email}`} onClick={() => testProxy(s.id)}
                            disabled={rowBusy[s.id]} variant="outline" size="sm"
                            className="border-[#2A2A2A] h-8 text-[11px]">
                      <ShieldCheck size={13} className="mr-1" /> Proxy
                    </Button>
                    <Button data-testid={`oauth-test-token-${s.email}`} onClick={() => testToken(s.id)}
                            disabled={rowBusy[s.id]} size="sm"
                            className="bg-[#00A4EF] text-white hover:bg-[#0090d0] h-8 text-[11px]">
                      <Lightning size={13} className="mr-1" /> {(testTo[s.id] || "").trim() ? "Send test" : "Test token"}
                    </Button>
                    <Button data-testid={`oauth-delete-${s.email}`} onClick={() => del(s.id, s.email)}
                            disabled={rowBusy[s.id]} variant="ghost" size="sm"
                            className="text-[#FF6B6B] hover:text-[#FF3B30] h-8">
                      <Trash size={14} />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function PreviewStat({ label, v, tone = "muted" }) {
  const color = tone === "brand" ? "text-[#E1FF00]" : tone === "error" ? "text-[#FF6B6B]"
    : tone === "warning" ? "text-[#FF9500]" : "text-white";
  return (
    <div className="border border-[#2A2A2A] bg-[#050505] px-3 py-2">
      <div className="micro">{label}</div>
      <div className={`font-heading text-2xl font-bold ${color}`}>{v}</div>
    </div>
  );
}

function ProxyBadge({ status }) {
  const map = {
    valid: ["text-[#00FF66] border-[#00FF66]", CheckCircle, "valid"],
    not_tested: ["text-[#8E8E93] border-[#3A3A3A]", Warning, "not tested"],
  };
  const [cls, Icon, label] = map[status] || ["text-[#FF6B6B] border-[#FF6B6B]", XCircle, status || "failed"];
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 border text-[10px] font-mono uppercase ${cls}`}>
      <Icon size={11} weight="fill" /> {label}
    </span>
  );
}

function OAuthBadge({ status }) {
  const reauth = status === "reauth_required";
  const cls = reauth ? "text-[#FF6B6B] border-[#FF6B6B]" : "text-[#00E5FF] border-[#00E5FF]";
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 border text-[10px] font-mono uppercase ${cls}`}>
      {reauth ? "reauth needed" : (status || "active")}
    </span>
  );
}
